import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { observer } from 'mobx-react'
import PropTypes from 'prop-types'
import { useContext, useMemo, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'

import { ConfirmButton } from '@/components/ConfirmButton'
import { Button } from '@/components/elements/button'
import { Checkbox } from '@/components/elements/checkbox'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Input } from '@/components/elements/input'
import { Textarea } from '@/components/elements/textarea'
import { InstructorCertifications } from '@/components/InstructorCertifications'
import { MultiSelect } from '@/components/MultiSelect'
import { SlidePanel } from '@/components/SlidePanel'
import { addEntityRole, updateEntityRole, reinviteEntityRole } from '@/services/entity.service'
import { updateUser } from '@/services/user.service'
import { UserStoreContext } from '@/stores/UserStore'
import { ROLE_OPTIONS } from '@/utils/constants'
import { cn, toast } from '@/utils/helpers'
import { formatUserName } from '@/utils/userHelpers'

import SetPasswordDialog from './SetPasswordDialog'

/**
 * Parses a comma-delimited string of emails into a trimmed, non-empty array.
 */
const parseEmails = (raw) =>
  (raw || '')
    .split(',')
    .map((e) => e.trim())
    .filter(Boolean)

const MAX_BULK_EMAILS = 50
const BATCH_SIZE = 5

/**
 * Processes an array of items in sequential batches of BATCH_SIZE,
 * running each batch concurrently with Promise.allSettled.
 */
const processInBatches = (items, fn) =>
  items.reduce((chain, _, i) => {
    if (i % BATCH_SIZE !== 0) return chain
    const batch = items.slice(i, i + BATCH_SIZE)
    return chain.then((prev) => Promise.allSettled(batch.map(fn)).then((res) => [...prev, ...res]))
  }, Promise.resolve([]))

// Schema for adding new users (single or bulk)
const AddUserSchema = z
  .object({
    emails: z.string().min(1, 'Please enter at least one email address'),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    roles: z.array(z.string()).min(1, 'Please select at least one role'),
    jobs: z.array(z.string()).optional(),
  })
  .refine((data) => parseEmails(data.emails).length > 0, {
    message: 'Please enter at least one email address',
    path: ['emails'],
  })
  .refine((data) => parseEmails(data.emails).length <= MAX_BULK_EMAILS, {
    message: `You can add up to ${MAX_BULK_EMAILS} users at a time`,
    path: ['emails'],
  })
  .refine(
    (data) => {
      const emailList = parseEmails(data.emails)
      return emailList.every((email) => z.string().email().safeParse(email).success)
    },
    {
      message: 'All emails must be valid email addresses',
      path: ['emails'],
    },
  )
  .refine(
    (data) => {
      const emailList = parseEmails(data.emails)
      // If single email, firstName is required
      if (emailList.length === 1) {
        return data.firstName && data.firstName.trim().length > 0
      }
      return true
    },
    {
      message: 'First name is required',
      path: ['firstName'],
    },
  )
  .refine(
    (data) => {
      const emailList = parseEmails(data.emails)
      // If single email, lastName is required
      if (emailList.length === 1) {
        return data.lastName && data.lastName.trim().length > 0
      }
      return true
    },
    {
      message: 'Last name is required',
      path: ['lastName'],
    },
  )

// Schema for editing existing users
const EditUserSchema = z.object({
  id: z.string(),
  email: z.string().email('Please enter a valid email address'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  roles: z.array(z.string()).min(1, 'Please select at least one role'),
  jobs: z.array(z.string()).optional(),
  isXverseAdmin: z.boolean().optional(),
  isXverseInstructor: z.boolean().optional(),
  isXverseInstructorCandidate: z.boolean().optional(),
})

const DEFAULT_ADD_VALUES = {
  emails: '',
  firstName: '',
  lastName: '',
  roles: ['Member'],
  jobs: [],
}

const AddEditUserSlideout = observer(({ editUser, entityId, jobs, queryKey, resetAndClose }) => {
  const queryClient = useQueryClient()
  const { user: currentUser, isXverseAdmin: currentUserIsXverseAdmin } =
    useContext(UserStoreContext)

  // Xverse admins can edit platform-level role flags from this slideout in
  // addition to the entity role itself. For everyone else those flags are
  // managed from the Admin > Users area and shown read-only here.
  const canEditXverseRoles = Boolean(editUser) && currentUserIsXverseAdmin

  // Compute default values based on edit vs add mode
  const defaultValues = useMemo(() => {
    if (!editUser) return DEFAULT_ADD_VALUES

    const {
      email,
      firstName,
      lastName,
      isXverseAdmin,
      isXverseInstructor,
      isXverseInstructorCandidate,
    } = editUser.user ?? {}
    return {
      id: editUser.id,
      email: email || '',
      firstName: firstName || '',
      lastName: lastName || '',
      roles: editUser.roles || [],
      jobs: editUser.jobs?.map((j) => j.id) || [],
      isXverseAdmin: Boolean(isXverseAdmin),
      isXverseInstructor: Boolean(isXverseInstructor),
      isXverseInstructorCandidate: Boolean(isXverseInstructorCandidate),
    }
  }, [editUser])

  const form = useForm({
    resolver: editUser ? zodResolver(EditUserSchema) : zodResolver(AddUserSchema),
    mode: 'onTouched',
    defaultValues,
  })

  // Watch emails field to determine if we show name fields (only in add mode)
  const emailsValue = useWatch({ control: form.control, name: 'emails' })
  const emailList = parseEmails(emailsValue)
  const isSingleEmail = emailList.length === 1
  const showNameFields = !editUser && isSingleEmail

  // State for reinvite
  const [reinviteStatus, setReinviteStatus] = useState(null)
  const [reinviteErrorMessage, setReinviteErrorMessage] = useState('')

  // State for password dialog
  const [showPasswordDialog, setShowPasswordDialog] = useState(false)

  const resetForm = () => {
    form.reset(editUser ? defaultValues : DEFAULT_ADD_VALUES)
    resetAndClose()
  }

  // Handlers
  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')

  // EDIT mutation. When the current user is an Xverse Admin we also patch the
  // user's platform-level role flags (admin/instructor/candidate) before the
  // entity role update, so both writes are sent against fresh user state.
  const { isPending: pendingEdit, mutate: mutateEdit } = useMutation({
    mutationFn: async (data) => {
      if (canEditXverseRoles) {
        await updateUser({
          id: editUser.user.id,
          isXverseAdmin: data.isXverseAdmin,
          isXverseInstructor: data.isXverseInstructor,
          isXverseInstructorCandidate: data.isXverseInstructorCandidate,
        })
      }
      return updateEntityRole({ entityId, ...data })
    },
    onError: (err) => handleError(err.message),
    onSuccess: (data, variables) => {
      handleSuccess(`${variables.firstName} ${variables.lastName} updated successfully.`)
      resetForm()
      queryClient.invalidateQueries({ queryKey })
    },
  })

  // ADD mutation (handles single or bulk)
  const { isPending: pendingAdd, mutate: mutateAdd } = useMutation({
    mutationFn: async (data) => {
      const emails = parseEmails(data.emails)

      if (emails.length === 1) {
        // Single user add - include name fields
        return addEntityRole({
          entityId,
          email: emails[0],
          firstName: data.firstName,
          lastName: data.lastName,
          roles: data.roles,
          jobs: data.jobs || [],
        })
      }

      // Bulk add - process in batches
      const results = await processInBatches(emails, (email) =>
        addEntityRole({
          entityId,
          email,
          roles: data.roles,
          jobs: data.jobs || [],
        }).then(() => email),
      )

      const succeeded = []
      const failed = []
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') {
          succeeded.push(r.value)
        } else {
          failed.push({ email: emails[i], reason: r.reason?.message || 'Unknown error' })
        }
      })

      return { succeeded, failed, isBulk: true }
    },
    onError: (err) => handleError(err.message),
    onSuccess: (data) => {
      // Single user add
      if (!data.isBulk) {
        handleSuccess(`${data.user.firstName} ${data.user.lastName} added successfully.`)
        resetForm()
        queryClient.invalidateQueries({ queryKey })
        return
      }

      // Bulk add results
      const { succeeded, failed } = data

      if (failed.length === 0) {
        const message =
          succeeded.length === 1
            ? '1 user added successfully'
            : `${succeeded.length} users added successfully`
        handleSuccess(message)
        resetForm()
        queryClient.invalidateQueries({ queryKey })
      } else if (succeeded.length === 0) {
        handleError(
          `All ${failed.length} users failed to add. Please review the emails and try again.`,
        )
      } else {
        // Partial success
        queryClient.invalidateQueries({ queryKey })
        toast(
          `${succeeded.length} users added, ${failed.length} failed. Please review the remaining emails and try again.`,
          'error',
        )
        // Leave panel open with only the failed emails
        form.setValue('emails', failed.map((f) => f.email).join(', '))
      }
    },
  })

  // REINVITE mutation
  const { isPending: pendingReinvite, mutate: reinviteUserMutation } = useMutation({
    mutationFn: (roleId) => reinviteEntityRole({ entityId, id: roleId }),
    onError: (err) => {
      setReinviteStatus('error')
      setReinviteErrorMessage(err.message)
      handleError(err.message)
    },
    onSuccess: () => {
      setReinviteStatus('success')
      handleSuccess('User reinvited successfully')
      queryClient.invalidateQueries({ queryKey })
    },
  })

  const isPending = pendingEdit || pendingAdd

  const onSubmit = (data) => {
    if (editUser) {
      mutateEdit(data)
    } else {
      mutateAdd(data)
    }
  }

  const jobOptions = jobs.map((j) => ({ label: j.title, id: j.id }))

  // Determine panel title and button label
  const getTitle = () => {
    if (editUser) return 'Update User'
    if (emailList.length > 1) return `Add ${emailList.length} Users`
    return 'Add User'
  }

  const getSubmitLabel = () => {
    if (editUser) return 'Update User'
    if (emailList.length > 1) return `Add ${emailList.length} Users`
    return 'Add User'
  }

  return (
    <>
      <SlidePanel
        actions={[
          {
            label: 'Cancel',
            onClick: () => resetAndClose(),
            type: 'cancel',
          },
          {
            label: getSubmitLabel(),
            onClick: form.handleSubmit(onSubmit),
            type: 'submit',
          },
        ]}
        content={
          <Form {...form}>
            <form className="flex flex-col gap-6" onSubmit={form.handleSubmit(onSubmit)}>
              {/* Reinvite section - edit mode only, user hasn't logged in */}
              {editUser && editUser.user?.lastLogin === null && (
                <div
                  className={cn(
                    'flex flex-col gap-2 rounded-lg border-1 p-4',
                    reinviteStatus === 'success' && 'border-green-500 bg-green-50',
                    reinviteStatus === 'error' && 'border-red-500 bg-red-50',
                    !reinviteStatus && 'bg-muted',
                  )}
                >
                  {reinviteStatus === 'success' && (
                    <span className="text-sm text-green-700">
                      Invitation email has been resent successfully!
                    </span>
                  )}
                  {reinviteStatus === 'error' && (
                    <>
                      <span className="text-sm font-semibold text-red-700">
                        Failed to resend invitation
                      </span>
                      <span className="text-sm text-red-600">{reinviteErrorMessage}</span>
                    </>
                  )}
                  {!reinviteStatus && (
                    <span className="text-sm">
                      This user has not logged in yet. You can resend the invitation email.
                    </span>
                  )}
                  {reinviteStatus !== 'success' && (
                    <ConfirmButton
                      confirmContent={`Are you sure you want to reinvite: ${formatUserName(editUser)}?`}
                      confirmTitle="Reinvite User"
                      className="self-start"
                      variant="outline"
                      loading={pendingReinvite}
                      onClick={() => reinviteUserMutation(editUser.id)}
                    >
                      Reinvite User
                    </ConfirmButton>
                  )}
                </div>
              )}

              {/* Password management section - edit mode only */}
              {editUser && (
                <div className="bg-muted flex flex-col gap-4 rounded-lg border-1 p-4">
                  <span className="text-sm font-semibold">Password Management</span>
                  <span className="text-muted-foreground text-sm">
                    Set a new password for this user. They will be notified via email.
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    className="self-start"
                    onClick={() => setShowPasswordDialog(true)}
                  >
                    Set New Password
                  </Button>
                </div>
              )}

              {/* User Details section */}
              <div className="flex flex-col gap-4">
                <span className="leading-6 font-bold">User Details</span>

                {/* Hidden ID field for edit mode */}
                {editUser && (
                  <FormField
                    control={form.control}
                    name="id"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input type="hidden" {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                )}

                {/* Email field - different for edit vs add mode */}
                {editUser ? (
                  <FormField
                    control={form.control}
                    disabled
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input type="email" {...field} disabled />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ) : (
                  <FormField
                    control={form.control}
                    disabled={isPending}
                    name="emails"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email Address(es)</FormLabel>
                        <FormControl>
                          <Textarea
                            autoComplete="off"
                            placeholder="user@example.com or user1@example.com, user2@example.com"
                            rows={3}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                        <p className="text-muted-foreground text-sm">
                          Enter a single email or multiple emails separated by commas
                        </p>
                      </FormItem>
                    )}
                  />
                )}

                {/* Name fields - always shown in edit mode, conditionally in add mode */}
                {(editUser || showNameFields) && (
                  <div className="flex flex-row gap-4">
                    <FormField
                      control={form.control}
                      disabled={isPending}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem className="w-full">
                          <FormLabel>First Name</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      disabled={isPending}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem className="w-full">
                          <FormLabel>Last Name</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}

                {/* Info message when adding multiple users */}
                {!editUser && emailList.length > 1 && (
                  <p className="text-muted-foreground text-sm">
                    Adding {emailList.length} users. They will receive invitation emails and set
                    their own names when they register.
                  </p>
                )}
              </div>

              {/* Account Access section */}
              <div className="flex flex-col gap-4">
                <span className="leading-6 font-bold">Account Access</span>

                <div className="flex flex-col gap-4 py-1 md:flex-row md:items-start">
                  <FormField
                    control={form.control}
                    disabled={isPending}
                    name="roles"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>Roles</FormLabel>
                        <FormControl>
                          <MultiSelect
                            id="roles"
                            placeholder="Select Roles"
                            options={ROLE_OPTIONS}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    disabled={isPending}
                    name="jobs"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>Jobs</FormLabel>
                        <FormControl>
                          <MultiSelect
                            id="jobs"
                            placeholder="Select Jobs"
                            options={jobOptions}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Xverse Roles — editable for Xverse Admins, read-only otherwise */}
                {editUser && canEditXverseRoles && (
                  <div className="flex flex-col gap-2 pt-1">
                    <FormLabel>Xverse Roles</FormLabel>
                    <FormField
                      control={form.control}
                      disabled={isPending}
                      name="isXverseAdmin"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center gap-2">
                          <FormControl>
                            <Checkbox
                              id="isXverseAdmin"
                              checked={field.value}
                              onCheckedChange={(checked) => field.onChange(checked)}
                              // Don't let a user revoke their own admin status.
                              disabled={editUser?.user?.id === currentUser?.id}
                            />
                          </FormControl>
                          <FormLabel className="text-sm font-normal">Xverse Admin</FormLabel>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      disabled={isPending}
                      name="isXverseInstructor"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center gap-2">
                          <FormControl>
                            <Checkbox
                              id="isXverseInstructor"
                              checked={field.value}
                              onCheckedChange={(checked) => field.onChange(checked)}
                            />
                          </FormControl>
                          <FormLabel className="text-sm font-normal">Xverse Instructor</FormLabel>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      disabled={isPending}
                      name="isXverseInstructorCandidate"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center gap-2">
                          <FormControl>
                            <Checkbox
                              id="isXverseInstructorCandidate"
                              checked={field.value}
                              onCheckedChange={(checked) => field.onChange(checked)}
                            />
                          </FormControl>
                          <FormLabel className="text-sm font-normal">
                            Xverse Instructor Candidate
                          </FormLabel>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}

                {editUser && !canEditXverseRoles && (
                  <div className="flex flex-col gap-4 pt-1 md:flex-row md:items-start">
                    <div className="flex-1">
                      <FormLabel>Xverse Instructor</FormLabel>
                      <div className="flex min-h-12 flex-row items-center gap-4">
                        <FormItem className="flex flex-row items-center gap-2">
                          <FormControl>
                            <Checkbox disabled checked={editUser?.user?.isXverseInstructor} />
                          </FormControl>
                          <FormLabel className="text-sm font-normal">Managed by RescueX</FormLabel>
                        </FormItem>
                      </div>
                    </div>
                  </div>
                )}

                {editUser?.user?.isXverseInstructor && (
                  <InstructorCertifications user={editUser.user} />
                )}
              </div>
            </form>
          </Form>
        }
        loading={isPending}
        title={getTitle()}
        setOpen={resetForm}
      />

      {editUser && showPasswordDialog && (
        <SetPasswordDialog
          user={editUser.user}
          entityId={entityId}
          roleId={editUser.id}
          open={showPasswordDialog}
          setOpen={setShowPasswordDialog}
          queryKey={queryKey}
        />
      )}
    </>
  )
})

AddEditUserSlideout.propTypes = {
  editUser: PropTypes.object,
  entityId: PropTypes.string.isRequired,
  jobs: PropTypes.array.isRequired,
  queryKey: PropTypes.array.isRequired,
  resetAndClose: PropTypes.func.isRequired,
}

export default AddEditUserSlideout
